import React, { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Link, Card, Grid, ListItemText, Typography } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { Dimensions, LoadingSpinner } from '../../../../components';
import { PublishableStatusComponent } from '../../../../components/PublishableStatusComponent';

import ModelDetailsCad from './ModelDetailsCad';
import { GQLAccessoryModel, GQLParty } from '@jewelutions/core-crm-server-api-client-domain';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { Exception } from '@jewelutions/core-common-domain';
import AccessoryModelUpdateDialog from '../AccessoryModelUpdateDialog';
import { PlatformNavigation } from '../../navigation/PlatformNavigation';
import { successNotification } from '../../../../core/utils';
import { toast } from 'react-toastify';
import { Box } from '../../../../types-hack';

interface IProps {
  party: GQLParty;
}

const AccessoryModelDetails: React.FC<IProps> = ({ party }) => {
  const [isUpdateAccessoryModelDialogOpen, setIsUpdateAccessoryModelDialogOpen] = useState(false);

  const { accessoryId, accessoryModelId } = PlatformNavigation.accessoryModelDetails.paramsFromPath(
    useRouteMatch()
  );

  const mapResponseToModel = (data: any): GQLAccessoryModel => {
    const accessoryModel = GQLOperationBuilder.accessory
      .accessoryAssetFromResponse(data)
      .accessory.models.find(model => model.id === accessoryModelId);
    if (accessoryModel === undefined)
      throw new Exception(`Cannot locate accessory model ${accessoryModelId} in GQL accessory data`);
    return accessoryModel;
  };

  const { loading, error, refetch, networkStatus, data } = GQLOperationBuilder.accessory.getAccessory({
    accessoryId: accessoryId
  });

  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  return (
    <div>
      <Card>
        <Box p={5}>
          <div style={{ display: 'flex' }}>
            <Typography style={{ marginRight: '2px' }} variant={'h6'}>
              Accessory Model Details
            </Typography>
            <EditIcon
              fontSize="small"
              style={{ cursor: 'pointer' }}
              onClick={() => setIsUpdateAccessoryModelDialogOpen(true)}
            />
          </div>

          <PublishableStatusComponent
            text="Accessory model publishable"
            status={mapResponseToModel(data).isPublishable}
          ></PublishableStatusComponent>

          <Grid container direction="row" justify={'space-between'}>
            <Grid item xs={12} md={4}>
              <Grid container direction="column" justify="center">
                <ListItemText
                  primary="Dimension"
                  secondary={<Dimensions dimensions3D={mapResponseToModel(data).dimensions} />}
                />
                <ListItemText primary="Size" secondary={mapResponseToModel(data).sizeType} />
                {mapResponseToModel(data).hasCadFileValid && (
                  <Link href={mapResponseToModel(data).cadFile!.url!}>Download accessory CAD</Link>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Card style={{ margin: '8px 0' }}>
        <Box p={5}>
          <div style={{ display: 'flex' }}>
            <Typography style={{ marginRight: '2px' }} variant={'h6'}>
              Model Availability
            </Typography>
          </div>

          <Grid container direction="column" justify="center">
            {mapResponseToModel(data).availableConfigurations.map(ac => {
              return (
                <ListItemText
                  primary={`${ac.platingType}, ${ac.materialType}, ${ac.sizeType}`}
                  secondary={`Weight: ${
                    mapResponseToModel(data).weights.find(
                      w => w.configuration.platingType === ac.platingType
                    )!.weightInGrams
                  }g`}
                />
              );
            })}
          </Grid>
        </Box>
      </Card>

      <ModelDetailsCad accessoryId={accessoryId} model={mapResponseToModel(data)} />

      {isUpdateAccessoryModelDialogOpen && (
        <AccessoryModelUpdateDialog
          accessoryId={accessoryId}
          accessoryModel={mapResponseToModel(data)}
          party={party}
          onClose={success => {
            if (success === true) {
              successNotification(toast, 'Accessory model updated successfully', () => {});
            }

            setIsUpdateAccessoryModelDialogOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default AccessoryModelDetails;
